import type { User } from '@qogita/canary-types';
import { createContext, useContext, useEffect, useState } from 'react';

import { useGetUser } from '#hooks/api/useGetUser';
import { getIsValidPortalUser } from '#src/utils/getIsValidPortalUser';
import { isQogitaUserEmail } from '#src/utils/isQogitaUserEmail';
import { identifyUser } from '#src/utils/report/tracking';

import { useLogout } from './Authentication';

export type ClientUser = User & { isQogitaUser: boolean };

type UserContext = ClientUser | null;

export const UserContext = createContext<UserContext>(null);

export const UserProvider = (props: {
  children: JSX.Element | JSX.Element[];
}): JSX.Element => {
  const logout = useLogout();
  const [user, setUser] = useState<UserContext | null>(null);

  const { data, error, fetchStatus } = useGetUser();

  useEffect(() => {
    // Only set the user to null if network is idle and stale data available
    if (!data && fetchStatus === 'idle') setUser(null);
  }, [fetchStatus, data]);

  useEffect(() => {
    if (data) {
      const isQogitaUser = isQogitaUserEmail(data.email);

      if (getIsValidPortalUser(data)) {
        setUser({
          ...data,
          isQogitaUser,
        });
      } else {
        logout();
      }
    }
  }, [data, logout]);

  useEffect(() => {
    if (user?.qid) {
      identifyUser(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.qid]);

  useEffect(() => {
    if (error) logout();
  }, [error, logout]);

  return <UserContext.Provider {...props} value={user} />;
};

export const useUserContext = (): UserContext => {
  return useContext(UserContext);
};

export const useUser = () => {
  const user = useContext(UserContext);

  if (!user) {
    throw new Error(
      'useUser must be called only when UserProvider has an User.',
    );
  }

  return user;
};
